import React, { useState } from 'react';
import { TextField, Button, FormControlLabel, MenuItem, Switch } from '@mui/material';
import DrawerShell from './DrawerShell';
import { FieldGroup } from '../PanelComerciales.styles';
import { panelTheme } from '../PanelComerciales.theme';
import { Team } from '../PanelComerciales.types';
import { isCurrency, isNonEmptyTeamName } from '../utils/validators';
import { formatCurrencyInput } from '../utils/currency';

interface CreateTeamDrawerProps {
    open: boolean;
    onClose: () => void;
    existingNames: string[];
    /** Nombres de comerciales ACTIVOS elegibles como líder (reemplaza texto libre). */
    leaderOptions: string[];
    onSave: (team: Team) => void;
}

const CreateTeamDrawer: React.FC<CreateTeamDrawerProps> = ({
    open,
    onClose,
    existingNames,
    leaderOptions,
    onSave,
}) => {
    const [name, setName] = useState('');
    const [leader, setLeader] = useState('');
    const [goal, setGoal] = useState('');
    const [active, setActive] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const reset = () => {
        setName('');
        setLeader('');
        setGoal('');
        setActive(true);
        setError(null);
    };

    const close = () => {
        reset();
        onClose();
    };

    const submit = () => {
        if (!isNonEmptyTeamName(name)) return setError('Nombre de equipo requerido');
        if (existingNames.some((n) => n.toLowerCase() === name.trim().toLowerCase())) {
            return setError('Ya existe un equipo con ese nombre');
        }
        if (!isNonEmptyTeamName(leader)) return setError('Líder requerido');
        if (goal && !isCurrency(goal)) return setError('Meta inválida (ej: $1,200,000)');

        onSave({
            name: name.trim(),
            leader: leader.trim(),
            members: 0,
            goal: goal.trim(),
            active,
        });
        close();
    };

    return (
        <DrawerShell
            open={open}
            title='Nuevo equipo'
            description='Completa la información y guarda los cambios.'
            onClose={close}
        >
            <FieldGroup>
                <label>Nombre del equipo</label>
                <TextField
                    size='small'
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder='Ej: Team Alpha'
                />
            </FieldGroup>
            <FieldGroup>
                <label>Líder</label>
                <TextField
                    size='small'
                    select
                    value={leader}
                    onChange={(e) => setLeader(e.target.value)}
                    SelectProps={{ displayEmpty: true }}
                    helperText={leaderOptions.length === 0 ? 'No hay comerciales activos' : undefined}
                >
                    <MenuItem value='' disabled>
                        Selecciona un comercial activo
                    </MenuItem>
                    {leaderOptions.map((name) => (
                        <MenuItem key={name} value={name}>
                            {name}
                        </MenuItem>
                    ))}
                </TextField>
            </FieldGroup>
            <FieldGroup>
                <label>Meta Q2</label>
                <TextField
                    size='small'
                    value={goal}
                    onChange={(e) => setGoal(formatCurrencyInput(e.target.value))}
                    inputProps={{ inputMode: 'numeric' }}
                    placeholder='$300,000'
                />
            </FieldGroup>
            <FormControlLabel
                control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} />}
                label='Activo'
            />
            {error ? <span style={{ color: panelTheme.colors.danger, fontSize: 12 }}>{error}</span> : null}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <Button onClick={close} sx={{ textTransform: 'none' }}>
                    Cancelar
                </Button>
                <Button
                    onClick={submit}
                    variant='contained'
                    sx={{
                        textTransform: 'none',
                        background: panelTheme.colors.indigo,
                        '&:hover': { background: panelTheme.colors.violet },
                    }}
                >
                    Guardar
                </Button>
            </div>
        </DrawerShell>
    );
};

export default CreateTeamDrawer;
